import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  CheckmarkCircle20Filled,
  Search20Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
} from "@fluentui/react-components";
import {
  SectionWrapper,
  SectionTitle,
  ProposalDataGrid,
  SpanTwo,
} from "./SharedSectionStyles";
import CompactField from "components/CompactField/CompactField";
import { getClientTypeOptions, getRegionOptions } from "./optionLists";
import { useTaxonomy } from "library/utils/useTaxonomy";
import { useTaxonomyFieldConverter } from "library/utils/useTaxonomyValue";
import { formatSicCodeLabel, getSicCodeNumber } from "library/utils/sicCode";
import { ValidationErrorDisplay } from "components/ValidationErrorDisplay";
import { ClientSearchInput } from "components/ClientSearchInput";
import { ClientSearchResult } from "state/queries/useClientSearch";
import { populateClientDataFromGOS } from "./clientDataHelpers";
import { useDriverDataSync } from "./useDriverDataSync";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  //margin-bottom: 16px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: normal;
  gap: 8px;
`;

const SearchButton = styled(Button)`
  min-width: auto;
  padding: 4px 8px;
  height: 28px;
  margin-top: -2px;
`;

const StyledDialogSurface = styled(DialogSurface)`
  min-height: 400px;
  max-height: 80vh;
`;

const DialogContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 500px;
  max-width: 600px;
  min-height: 300px;
`;

const GOSIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.accent};
  background: ${({ theme }) => theme.palette.neutralLight || "#e6f4ea"};
  padding: 6px 12px;
  border-radius: 8px;

  svg {
    color: ${({ theme }) => theme.palette.accent};
  }
`;

const ClearGOSButton = styled(Button)`
  min-width: auto;
  padding: 2px;
  height: 20px;

  &:hover {
    background: ${({ theme }) => theme.palette.neutralLighter};
  }
`;

interface ClientDataSectionProps {
  isCasco?: boolean;
  getError?: (fieldPath: string) => string | undefined;
  isPrefilling?: boolean;
  offerId?: string | null;
}

const ClientDataSection: React.FC<ClientDataSectionProps> = ({
  isCasco = false,
  getError,
  isPrefilling = false,
  offerId,
}) => {
  const { t } = useTranslation();
  const taxonomy = useTaxonomy();
  const { watch, setValue } = useFormContext();
  const syncDriverData = useDriverDataSync();
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);

  // Watch client type to conditionally show fields
  const clientType = watch("clientData.clientType");
  const isCompany = clientType === "company";
  const isPerson = clientType === "person";
  const isExistingInGOS = watch("clientData.isExistingInGOS");
  const clientCode = watch("clientData.clientCode");

  // Background color for fields populated from GOS
  const readOnlyBackgroundColor = isExistingInGOS ? "#e6f3ff" : undefined;

  // Fields are read-only only when an offer already exists;
  // data loaded from GOS stays editable
  const isReadOnly = !!offerId;
  const isReadOnlyExceptGOS = !!offerId;

  // Remember the client code that came from GOS; if the user changes it,
  // the client no longer matches the GOS record so the "Existing in GOS" flag is dropped
  const gosClientCodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isExistingInGOS) {
      gosClientCodeRef.current = null;
      return;
    }
    if (gosClientCodeRef.current === null) {
      gosClientCodeRef.current = clientCode ?? "";
      return;
    }
    // If GOS had no code for this record, there is nothing to diverge from,
    // so keep the tag regardless of what the user types
    if (gosClientCodeRef.current === "") {
      return;
    }
    if ((clientCode ?? "") !== gosClientCodeRef.current) {
      gosClientCodeRef.current = null;
      setValue("clientData.isExistingInGOS", false);
    }
  }, [isExistingInGOS, clientCode, setValue]);

  // Handler to clear GOS client data
  const handleClearGOSClient = useCallback(() => {
    const clientDataFields = [
      "clientData.clientType",
      "clientData.isExistingInGOS",
      "clientData.clientName",
      "clientData.clientFirstName",
      "clientData.clientLastName",
      "clientData.clientCode",
      "clientData.resident",
      "clientData.clientEmail",
      "clientData.clientPhoneNumber",
      "clientData.address",
      "clientData.city",
      "clientData.cityAddress",
      "clientData.postIndex",
      "clientData.partnerMainSicCode",
    ];

    clientDataFields.forEach((field) => {
      if (field === "clientData.clientType") {
        setValue(field, "company");
      } else if (field === "clientData.isExistingInGOS") {
        setValue(field, false);
      } else {
        setValue(field, "");
      }
    });
  }, [setValue]);

  // Auto-convert partnerMainSicCode from ID to code when loading from GOS
  useTaxonomyFieldConverter("clientData.partnerMainSicCode", "SicCode" as any);

  // Get translated options
  const clientTypeOptions = getClientTypeOptions(t);
  const regionOptions = getRegionOptions(t);

  // Get SicCode options from taxonomy if available, otherwise empty
  const sicCodeOptions = useMemo(() => {
    // Try to get from taxonomy first
    const sicCodeTaxonomy = (taxonomy as any)?.SicCode;
    if (sicCodeTaxonomy?.items) {
      // Use ID as key (unique), but keep code as the value for form field
      // This ensures React has unique keys while form stores the correct code
      return sicCodeTaxonomy.items
        .map((item: any) => ({
          key: String(item.id), // Use ID as unique React key
          value: item.code, // Use code as form field value
          // Translations are keyed as TaxonomyName.Code (e.g. "SicCode.9811");
          // the numeric code is prefixed when the locale omits it.
          text: formatSicCodeLabel(item.code, t(item.code, item.code)),
        }))
        .sort((a: any, b: any) =>
          getSicCodeNumber(a.value).localeCompare(getSicCodeNumber(b.value)),
        );
    }

    // Return empty array if not available - the field will still work for GOS loaded clients
    return [];
  }, [taxonomy, t]);

  // Handler for client selection from search
  const handleClientSelect = useCallback(
    (client: ClientSearchResult) => {
      populateClientDataFromGOS(client, setValue);
      setIsSearchDialogOpen(false);
    },
    [setValue],
  );

  return (
    <SectionWrapper>
      <SectionHeader>
        <SectionTitleRow>
          <SectionTitle>
            {t("qtg.proposal.generalInfo.clientData")}
          </SectionTitle>
          <Dialog
            modalType="modal"
            open={isSearchDialogOpen}
            onOpenChange={(_, data) => setIsSearchDialogOpen(data.open)}
          >
            <DialogTrigger disableButtonEnhancement>
              <SearchButton
                data-help="offer-general-client-search"
                appearance="subtle"
                icon={<Search20Regular />}
                title={t(
                  "qtg.proposal.clientData.searchClient",
                  "Search client",
                )}
                disabled={!!offerId}
              />
            </DialogTrigger>
            <StyledDialogSurface>
              <DialogBody>
                <DialogTitle
                  action={
                    <Button
                      appearance="subtle"
                      aria-label={t("qtg.validation.close", "Close")}
                      icon={<Dismiss24Regular />}
                      onClick={() => setIsSearchDialogOpen(false)}
                    />
                  }
                >
                  {t(
                    "qtg.proposal.clientData.gosClientSearch",
                    "GOS client search",
                  )}
                </DialogTitle>
                <DialogContent>
                  <DialogContentWrapper>
                    <ClientSearchInput onClientSelect={handleClientSelect} />
                  </DialogContentWrapper>
                </DialogContent>
              </DialogBody>
            </StyledDialogSurface>
          </Dialog>
        </SectionTitleRow>
        {isExistingInGOS && (
          <GOSIndicator>
            <CheckmarkCircle20Filled />
            {t("qtg.proposal.clientData.existingInGOS", "Existing in GOS")}
            <ClearGOSButton
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={handleClearGOSClient}
              title={t(
                "qtg.proposal.clientData.clearGOSClient",
                "Clear GOS client",
              )}
              aria-label={t(
                "qtg.proposal.clientData.clearGOSClient",
                "Clear GOS client",
              )}
            />
          </GOSIndicator>
        )}
      </SectionHeader>
      <ProposalDataGrid>
        {/* Row 1: Client type with checkboxes and bonus malus */}
        <SpanTwo>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 5fr",
              gap: "12px 16px",
              alignItems: "end",
            }}
          >
            <CompactField
              type="dropdown"
              name="clientData.clientType"
              dataHelp="offer-general-client-type"
              label="Client type"
              translationKey="qtg.proposal.clientData.clientType"
              options={clientTypeOptions}
              defaultValue="company"
              readOnly={isReadOnly}
              backgroundColor={readOnlyBackgroundColor}
              required={true}
            />
            {isCompany && (
              <div>
                <CompactField
                  type="input"
                  name="clientData.clientName"
                  dataHelp="offer-general-client-name"
                  label="Client name"
                  translationKey="qtg.proposal.clientData.clientName"
                  readOnly={isReadOnly}
                  backgroundColor={readOnlyBackgroundColor}
                  required={true}
                />
                {getError && (
                  <ValidationErrorDisplay error={getError("Client.Name")} />
                )}
              </div>
            )}
            {isPerson && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px 16px",
                }}
              >
                <div>
                  <CompactField
                    type="input"
                    name="clientData.clientFirstName"
                    dataHelp="offer-general-client-first-name"
                    label="First name"
                    translationKey="qtg.proposal.clientData.clientFirstName"
                    readOnly={isReadOnly}
                    backgroundColor={readOnlyBackgroundColor}
                    required={true}
                  />
                  {getError && (
                    <ValidationErrorDisplay
                      error={getError("Client.FirstName")}
                    />
                  )}
                </div>
                <div>
                  <CompactField
                    type="input"
                    name="clientData.clientLastName"
                    dataHelp="offer-general-client-last-name"
                    label="Last name"
                    translationKey="qtg.proposal.clientData.clientLastName"
                    readOnly={isReadOnly}
                    backgroundColor={readOnlyBackgroundColor}
                    required={true}
                  />
                  {getError && (
                    <ValidationErrorDisplay
                      error={getError("Client.LastName")}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </SpanTwo>
        <div>
          <CompactField
            type="input"
            name="clientData.clientCode"
            dataHelp="offer-general-client-code"
            label="Client code"
            translationKey="qtg.proposal.clientData.clientCode"
            readOnly={isReadOnly}
            backgroundColor={readOnlyBackgroundColor}
            required={true}
          />
          {getError && (
            <ValidationErrorDisplay error={getError("Client.Code")} />
          )}
        </div>
        {/* Row 2: Client name and client code */}
        {/* Client name with tan/beige background */}
        {/* Client code with tan/beige background */}
        {/* Row 3: Client email and licence date */}
        {/* Client age and driving experience */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          {/* Row 6: City and social discount */}

          <CompactField
            type="input"
            name="clientData.city"
            label="City"
            translationKey="qtg.proposal.clientData.city"
            inputStyle={{ width: "130px" }}
            readOnly={isReadOnly}
            backgroundColor={readOnlyBackgroundColor}
          />
          <CompactField
            type="input"
            name="clientData.postIndex"
            label="Post index"
            translationKey="qtg.proposal.clientData.postIndex"
            inputStyle={{ width: "130px" }}
            readOnly={isReadOnly}
            backgroundColor={readOnlyBackgroundColor}
          />
        </div>
        <SpanTwo>
          <CompactField
            type="dropdown"
            name="clientData.sicCode"
            label={t("qtg.proposal.clientData.partnerMainSicCode", "SIC Code")}
            translationKey="qtg.proposal.clientData.partnerMainSicCode"
            options={sicCodeOptions}
            readOnly={isReadOnly}
            backgroundColor={readOnlyBackgroundColor}
            placeholder={
              sicCodeOptions.length === 0
                ? t("greco.noOptions", "No options")
                : undefined
            }
          />
        </SpanTwo>
        {/* Row 8: Address and other driver's licence date */}
        <div>
          <CompactField
            type="input"
            name="clientData.address"
            dataHelp="offer-general-client-address"
            label="Address"
            translationKey="qtg.proposal.clientData.address"
            readOnly={isReadOnly}
            backgroundColor={readOnlyBackgroundColor}
            required={true}
          />
          {getError && (
            <ValidationErrorDisplay
              error={getError("Client.Address.AddressString")}
            />
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="input"
              name="clientData.clientEmail"
              dataHelp="offer-general-client-email"
              label="Client e-mail"
              translationKey="qtg.proposal.clientData.clientEmail"
              inputStyle={{ width: "130px" }}
              inputType="email"
              readOnly={isReadOnly}
              backgroundColor={readOnlyBackgroundColor}
              required={true}
            />
            {getError && (
              <ValidationErrorDisplay
                error={getError("Client.Address.Email")}
              />
            )}
          </div>
          <div>
            <CompactField
              type="input"
              name="clientData.clientPhoneNumber"
              dataHelp="offer-general-client-phone"
              label="Client phone number"
              translationKey="qtg.proposal.clientData.clientPhoneNumber"
              inputStyle={{ width: "130px" }}
              inputType="tel"
              readOnly={isReadOnly}
              backgroundColor={readOnlyBackgroundColor}
              required={true}
            />
            {getError && (
              <ValidationErrorDisplay
                error={getError("Client.Address.Phone")}
              />
            )}
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div style={{ alignSelf: "end", transform: "translateY(12px)" }}>
            <CompactField
              type="checkbox"
              name="clientData.vip"
              label="VIP"
              translationKey="qtg.proposal.clientData.vip"
              readOnly={isReadOnly}
            />
          </div>
          <div style={{ alignSelf: "end", transform: "translateY(12px)" }}>
            <CompactField
              type="checkbox"
              name="clientData.resident"
              dataHelp="offer-general-client-resident"
              label="Resident"
              translationKey="qtg.proposal.clientData.resident"
              defaultChecked={true}
              readOnly={isReadOnly}
            />
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="inputWithUnit"
              name="clientData.clientAge"
              dataHelp="offer-general-client-age"
              label="Client age"
              translationKey="qtg.proposal.clientData.clientAge"
              inputType="number"
              unit="years"
              inputStyle={{ width: "80px" }}
              readOnly={isReadOnlyExceptGOS}
              onBlur={
                isCasco
                  ? () => syncDriverData("clientData.clientAge")
                  : undefined
              }
            />
            {getError && (
              <ValidationErrorDisplay error={getError("Client.Age")} />
            )}
          </div>
          <div>
            <CompactField
              type="inputWithUnit"
              name="clientData.drivingExperience"
              dataHelp="offer-general-client-experience"
              label="Driving experience"
              translationKey="qtg.proposal.clientData.drivingExperience"
              inputType="number"
              unit="years"
              inputStyle={{ width: "80px" }}
              readOnly={isReadOnlyExceptGOS}
              onBlur={
                isCasco
                  ? () => syncDriverData("clientData.drivingExperience")
                  : undefined
              }
            />
            {getError && (
              <ValidationErrorDisplay error={getError("Client.Experience")} />
            )}
          </div>
        </div>
        {/* Row 4: Region and client age */}
        <CompactField
          type="dropdown"
          name="clientData.region"
          label="Region"
          translationKey="qtg.proposal.clientData.region"
          options={regionOptions}
          readOnly={isReadOnlyExceptGOS}
        />
      </ProposalDataGrid>
    </SectionWrapper>
  );
};

export default ClientDataSection;
